/**
 * Pure fit-score bucketing — deliberately a leaf module with ZERO imports.
 *
 * `fitScoreBucket` used to live in `fit-score-breakdown.ts`, which imports the
 * D1 query layer (`@/lib/db/queries/*`), `safeUnstableCache` and the 66 KB
 * scorer. When #4782 value-imported it from two `"use client"` components
 * (`ai-apply-badge.tsx` on every feed card, `low-fit-notice.tsx`), that whole
 * tree entered the client-component graph and the production worker grew
 * +320 KiB gzip in one merge (6.70 → 7.01 MiB), tripping `check:bundle-delta`
 * on Cloudflare Workers Builds and freezing every deploy from 2026-09-03
 * 05:13Z. GitHub CI never runs that guard on the production build, so the PR
 * looked fine.
 *
 * Anything a client component needs from `@/lib/match/*` as a VALUE belongs
 * here or in another import-free leaf; everything else stays type-only (see
 * the note in `src/components/jobs/fit-score-badge.tsx`).
 */

/**
 * Mirrors `STRONG_MATCH_FLOOR` in `score.ts` as a literal so this file stays
 * import-free. `tests/unit/lib/match/low-fit.test.ts` asserts the two agree.
 */
export const FIT_SCORE_STRONG_FLOOR = 60;

/** Skip-rate audit's documented "surfacing floor" (score.test.ts,
 * STRONG_MATCH_FLOOR test: "Floor must be >40 ... so the 40-59 band is
 * honestly framed as partial") — reused as the medium/low telemetry-bucket
 * split so `fit_score_shown`'s bucket boundaries track the same domain
 * constants the scorer itself already treats as meaningful. */
export const FIT_SCORE_BUCKET_MEDIUM_FLOOR = 40;

/**
 * Buckets a raw fit score for telemetry (`fit_score_shown`'s
 * `fit_score_bucket` property) and for the low-fit UI gate — never emit the
 * raw score itself.
 */
export function fitScoreBucket(score: number): "low" | "medium" | "high" {
	if (score >= FIT_SCORE_STRONG_FLOOR) return "high";
	if (score >= FIT_SCORE_BUCKET_MEDIUM_FLOOR) return "medium";
	return "low";
}
